import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BarChart3, ChevronDown, ChevronUp, Plus, Send, Settings, Trash2, Users } from 'lucide-react'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'

// ── Types (mirror the API surface in apps/api/src/modules/audience) ──────────

type AudiencePlatform =
  | 'hivecells' | 'ga4' | 'search_console' | 'meta' | 'instagram' | 'youtube'
  | 'spotify' | 'chartmetric' | 'x' | 'tiktok' | 'bandsintown' | 'songkick'
  | 'lastfm' | 'sparktoro' | 'audiense' | 'similarweb'

type SurveyQuestionType =
  | 'single_choice' | 'multi_choice' | 'scale_1_5' | 'scale_1_10' | 'short_text' | 'opt_in'

type SurveyCadence =
  | 'one_time' | 'onboarding' | 'quarterly_pulse' | 'pre_tour' | 'post_show' | 'ad_hoc'

type CampaignStatus = 'draft' | 'queued' | 'live' | 'closed' | 'failed'

interface QuestionOption {
  value: string
  label: string
  bucket: string
}

interface SurveyQuestionInput {
  position: number
  questionType: SurveyQuestionType
  prompt: string
  subtext?: string
  options?: QuestionOption[]
  isRequired: boolean
  affinityTag?: string
}

interface SurveyTemplateRow {
  id: string
  name: string
  description: string | null
  cadence: SurveyCadence
  isActive: boolean
  updatedAt: string
  _count?: { questions: number; campaigns: number }
}

interface SurveyTemplateDetail extends SurveyTemplateRow {
  questions: Array<SurveyQuestionInput & { id: string }>
}

interface SurveyCampaignRow {
  id: string
  templateId: string
  hiveCellsCampaignId: string | null
  windowStart: string
  windowEnd: string
  targetSize: number | null
  completedCount: number
  status: CampaignStatus
  firedAt: string | null
  lastAggregateAt: string | null
  lastErrorMessage: string | null
  template?: { id: string; name: string; cadence: SurveyCadence }
}

interface LatestSnapshot {
  platform: AudiencePlatform
  snapshotDate: string
  totalReach: number | null
  engagementRate: number | null
}

interface CredentialStatus {
  id: string
  platform: AudiencePlatform
  displayLabel: string | null
  connectedAt: string
  lastUsedAt: string | null
  lastErrorAt: string | null
  lastErrorMessage: string | null
}

// ── Display helpers ──────────────────────────────────────────────────────────

const PLATFORM_LABEL: Record<AudiencePlatform, string> = {
  hivecells: 'HiveCells',
  ga4: 'Google Analytics 4',
  search_console: 'Google Search Console',
  meta: 'Facebook',
  instagram: 'Instagram',
  youtube: 'YouTube',
  spotify: 'Spotify',
  chartmetric: 'Chartmetric',
  x: 'X (Twitter)',
  tiktok: 'TikTok',
  bandsintown: 'Bandsintown',
  songkick: 'Songkick',
  lastfm: 'Last.fm',
  sparktoro: 'SparkToro',
  audiense: 'Audiense',
  similarweb: 'SimilarWeb',
}

const CADENCE_LABEL: Record<SurveyCadence, string> = {
  one_time: 'One-time',
  onboarding: 'Onboarding',
  quarterly_pulse: 'Quarterly pulse',
  pre_tour: 'Pre-tour',
  post_show: 'Post-show',
  ad_hoc: 'Ad-hoc',
}

const QUESTION_TYPE_LABEL: Record<SurveyQuestionType, string> = {
  single_choice: 'Single choice',
  multi_choice: 'Multiple choice',
  scale_1_5: 'Scale 1–5',
  scale_1_10: 'Scale 1–10',
  short_text: 'Short text',
  opt_in: 'Opt-in (yes/no)',
}

const STATUS_TONE: Record<CampaignStatus, string> = {
  draft: 'bg-zinc-100 text-zinc-700 ring-zinc-200',
  queued: 'bg-amber-50 text-amber-700 ring-amber-200',
  live: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  closed: 'bg-zinc-100 text-zinc-700 ring-zinc-200',
  failed: 'bg-red-50 text-red-700 ring-red-200',
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function AudiencePage() {
  const qc = useQueryClient()
  const [googleNotice, setGoogleNotice] = useState<{
    tone: 'success' | 'warning'
    message: string
  } | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const tiktokConnected = params.get('tiktokConnected')
    const tiktokError = params.get('tiktokError')
    const googleSuccess = params.get('googleSuccess')
    const googleError = params.get('googleError')

    if (tiktokConnected || tiktokError || googleSuccess || googleError) {
      void qc.invalidateQueries({ queryKey: ['audience-credentials'] })
    }

    if (googleSuccess === '1') {
      const connected = params.get('connected')?.split(',').filter(Boolean) ?? []
      const skipped = params.get('skipped')?.split(',').filter(Boolean) ?? []
      const ga4Reason = params.get('ga4Reason')

      if (skipped.includes('ga4')) {
        const reasonText = describeGa4SkipReason(ga4Reason)
        setGoogleNotice({
          tone: 'warning',
          message:
            `Connected: ${connected.join(', ') || 'none'}. GA4 was not linked — ${reasonText} `
            + 'You can enter the Property ID manually on the GA4 card below (Admin → Property settings).',
        })
      } else if (connected.length > 0) {
        setGoogleNotice({
          tone: 'success',
          message: `Google connected: ${connected.join(', ')}.`,
        })
      }
    }

    if (googleError) {
      setGoogleNotice({
        tone: 'warning',
        message: `Google connection failed: ${googleError}`,
      })
    }

    if (tiktokConnected || tiktokError || googleSuccess || googleError) {
      window.history.replaceState({}, '', '/audience')
    }
  }, [qc])

  return (
    <div className="space-y-8">
      <PageHeader
        title="Audience Intelligence"
        subtitle="First-party fan surveys, platform connections, and aggregated audience snapshots."
      />
      <div className="flex justify-end">
        <a
          href="/audience/map"
          className="inline-flex items-center gap-2 rounded border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
        >
          <BarChart3 className="h-4 w-4" />
          View audience map
        </a>
      </div>
      {googleNotice && (
        <div
          className={`rounded-lg border px-4 py-3 text-sm ${
            googleNotice.tone === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}
        >
          {googleNotice.message}
        </div>
      )}
      <ConnectionsSection />
      <ChartmetricInsightsSection />
      <SimilarArtistsSection />
      <SurveysSection />
      <SnapshotsSection />
    </div>
  )
}

// ── Connections ──────────────────────────────────────────────────────────────

/**
 * Compute the next auto-sync time in the user's local timezone.
 * Daily platforms sync at 04:00 UTC; Chartmetric syncs at 05:00 UTC on Mondays.
 */
function nextSyncLabel(platform: AudiencePlatform): string {
  const now = new Date()
  let next: Date

  if (platform === 'chartmetric') {
    // Next Monday at 05:00 UTC
    next = new Date(now)
    next.setUTCHours(5, 0, 0, 0)
    const daysUntilMonday = (8 - next.getUTCDay()) % 7 || 7
    next.setUTCDate(next.getUTCDate() + (now.getUTCDay() === 1 && now < next ? 0 : daysUntilMonday))
  } else {
    // Next 04:00 UTC (today if not yet passed, else tomorrow)
    next = new Date(now)
    next.setUTCHours(4, 0, 0, 0)
    if (next <= now) next.setUTCDate(next.getUTCDate() + 1)
  }

  const diffMs = next.getTime() - now.getTime()
  const diffH = Math.floor(diffMs / 3_600_000)
  const diffM = Math.floor((diffMs % 3_600_000) / 60_000)
  const localTime = next.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  const localDate = next.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })

  if (diffH < 1) return `in ${diffM}m (${localTime} today)`
  if (diffH < 24) return `in ${diffH}h ${diffM}m (${localTime} today)`
  return `${localDate} at ${localTime} (in ~${diffH}h)`
}

// Per-platform metadata controlling how the card behaves.
type ConnectMode =
  | { kind: 'oauth'; oauthStart: string }           // hits a /start endpoint that returns { url }
  | { kind: 'verify-and-save'; verifyPath: string } // posts an ID to verify, then saves
  | { kind: 'auto' }                                 // wired automatically (HiveCells webhook)
  | { kind: 'disabled'; reason: string }             // can't connect today, show reason

interface PlatformMeta {
  label: string
  tagline: string
  active: boolean   // false for stubs we hide behind the toggle
  connect: ConnectMode
}

// HiveCells is intentionally absent — its credential lives on the Integrations
// page. A read-only status banner renders above this grid via HiveCellsStatus.
const PLATFORM_META: Record<string, PlatformMeta> = {
  spotify: {
    label: 'Spotify',
    tagline: 'Followers, popularity, top tracks, related artists',
    active: true,
    connect: { kind: 'verify-and-save', verifyPath: '/audience/verify/spotify' },
  },
  meta: {
    label: 'Facebook',
    tagline: 'Page followers, reach, engagement, country breakdown',
    active: true,
    connect: { kind: 'oauth', oauthStart: '/audience-oauth/meta/start' },
  },
  instagram: {
    label: 'Instagram',
    tagline: 'Auto-attached when Facebook Page connects (linked IG Business)',
    active: true,
    connect: { kind: 'auto' },
  },
  ga4: {
    label: 'Google Analytics 4',
    tagline: 'Website audience, country breakdown, top pages',
    active: true,
    connect: { kind: 'oauth', oauthStart: '/audience-oauth/google/start' },
  },
  youtube: {
    label: 'YouTube',
    tagline: 'Channel subscribers, views, top countries',
    active: true,
    connect: { kind: 'oauth', oauthStart: '/audience-oauth/google/start' },
  },
  search_console: {
    label: 'Google Search Console',
    tagline: 'Search impressions, click-through, top queries',
    active: true,
    connect: { kind: 'oauth', oauthStart: '/audience-oauth/google/start' },
  },
  chartmetric: {
    label: 'Chartmetric',
    tagline: 'Monthly listeners, audience geography, cross-platform aggregate',
    active: true,
    connect: { kind: 'verify-and-save', verifyPath: '/audience/verify/chartmetric' },
  },
  tiktok: {
    label: 'TikTok',
    tagline: 'Followers and profile stats via TikTok Login Kit',
    active: true,
    connect: { kind: 'oauth', oauthStart: '/audience-oauth/tiktok/start' },
  },
  x: { label: 'X (Twitter)', tagline: 'Requires X Basic tier subscription', active: false, connect: { kind: 'disabled', reason: 'No X subscription' } },
  bandsintown: { label: 'Bandsintown', tagline: 'Not yet built', active: false, connect: { kind: 'disabled', reason: 'Connector not yet built' } },
  songkick: { label: 'Songkick', tagline: 'Not yet built', active: false, connect: { kind: 'disabled', reason: 'Connector not yet built' } },
  lastfm: { label: 'Last.fm', tagline: 'Not yet built', active: false, connect: { kind: 'disabled', reason: 'Connector not yet built' } },
  sparktoro: { label: 'SparkToro', tagline: 'Subscription not active', active: false, connect: { kind: 'disabled', reason: 'No subscription' } },
  audiense: { label: 'Audiense', tagline: 'Subscription not active', active: false, connect: { kind: 'disabled', reason: 'No subscription' } },
  similarweb: { label: 'SimilarWeb', tagline: 'Subscription not active', active: false, connect: { kind: 'disabled', reason: 'No subscription' } },
}

interface SpotifyVerifyResponse {
  spotifyArtistId: string
  name: string
  followers: number
  popularity: number
  imageUrl: string | null
}

function ConnectionsSection() {
  const qc = useQueryClient()
  const [showInactive, setShowInactive] = useState(false)

  const credsQ = useQuery<CredentialStatus[], ApiClientError>({
    queryKey: ['audience-credentials'],
    queryFn: () => artistFetch<CredentialStatus[]>('/audience/credentials'),
  })

  const connected = credsQ.data ?? []
  const connectedByPlatform = useMemo(
    () => new Map(connected.map((c) => [c.platform, c] as const)),
    [connected],
  )

  const platforms = Object.keys(PLATFORM_META) as AudiencePlatform[]
  const active = platforms.filter((p) => PLATFORM_META[p]!.active)
  const inactive = platforms.filter((p) => !PLATFORM_META[p]!.active)
  const activeConnected = active.filter((p) => connectedByPlatform.has(p)).length

  return (
    <section className="rounded-xl border border-zinc-200 bg-white">
      <header className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-zinc-500" />
          <h2 className="text-base font-semibold text-zinc-900">Platform connections</h2>
        </div>
        <span className="text-sm text-zinc-500">
          {activeConnected} / {active.length} connected
        </span>
      </header>

      <div className="grid grid-cols-1 gap-3 p-5 md:grid-cols-2">
        {active.map((p) => (
          <PlatformCard
            key={p}
            platform={p}
            meta={PLATFORM_META[p]!}
            credential={connectedByPlatform.get(p) ?? null}
            onChange={() => qc.invalidateQueries({ queryKey: ['audience-credentials'] })}
          />
        ))}
      </div>

      <div className="border-t border-zinc-100 px-5 py-3">
        <button
          type="button"
          onClick={() => setShowInactive((v) => !v)}
          className="inline-flex items-center gap-1 text-xs font-medium text-zinc-600 hover:text-zinc-900"
        >
          {showInactive ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          {showInactive ? 'Hide' : 'Show'} {inactive.length} inactive platform{inactive.length === 1 ? '' : 's'}
        </button>
        {showInactive && (
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            {inactive.map((p) => (
              <PlatformCard
                key={p}
                platform={p}
                meta={PLATFORM_META[p]!}
                credential={connectedByPlatform.get(p) ?? null}
                onChange={() => qc.invalidateQueries({ queryKey: ['audience-credentials'] })}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

function PlatformCard({
  platform,
  meta,
  credential,
  onChange,
}: {
  platform: AudiencePlatform
  meta: PlatformMeta
  credential: CredentialStatus | null
  onChange: () => void
}) {
  const isConnected = credential !== null
  const hasError = !!credential?.lastErrorMessage
  const isDisabled = meta.connect.kind === 'disabled'
  const [syncing, setSyncing] = useState(false)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)

  const handleSyncNow = async () => {
    setSyncing(true)
    setSyncMessage(null)
    try {
      await artistFetch('/audience/sync', {
        method: 'POST',
        body: { platform },
      })
      setSyncMessage('Sync queued — results in a few seconds.')
      setTimeout(() => {
        onChange()
        setSyncMessage(null)
      }, 3000)
    } catch (err) {
      setSyncMessage(err instanceof Error ? err.message : 'Sync failed')
    } finally {
      setSyncing(false)
    }
  }

  return (
    <div
      className={`flex flex-col gap-3 rounded-lg border p-4 ${
        isDisabled
          ? 'border-zinc-200 bg-zinc-50'
          : isConnected
            ? hasError
              ? 'border-red-200 bg-red-50/30'
              : 'border-emerald-200 bg-white'
            : 'border-zinc-200 bg-white'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span
              className={`inline-block h-2 w-2 rounded-full ${
                isConnected
                  ? hasError
                    ? 'bg-red-500'
                    : 'bg-emerald-500'
                  : isDisabled
                    ? 'bg-zinc-300'
                    : 'bg-zinc-300'
              }`}
            />
            <span className="font-semibold text-zinc-900">{meta.label}</span>
          </div>
          <p className="mt-1 text-xs text-zinc-600">{meta.tagline}</p>
        </div>
        {isConnected && credential?.displayLabel && (
          <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">
            {credential.displayLabel}
          </span>
        )}
      </div>

      {isConnected && (
        <div className="text-xs text-zinc-500">
          <div>Connected {formatDate(credential.connectedAt)}</div>
          {credential.lastUsedAt && <div>Last sync {formatDate(credential.lastUsedAt)}</div>}
          {credential.lastErrorMessage && (
            <div className="mt-1 rounded bg-red-100 px-2 py-1 text-red-800">
              {credential.lastErrorMessage}
            </div>
          )}
          {syncMessage && (
            <div className="mt-1 text-emerald-700">{syncMessage}</div>
          )}
          <div className="mt-1 text-zinc-400">
            Next auto-sync: {nextSyncLabel(platform)}
          </div>
          <button
            type="button"
            onClick={handleSyncNow}
            disabled={syncing}
            className="mt-1 rounded border border-zinc-300 bg-white px-2 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50 disabled:opacity-50"
          >
            {syncing ? 'Syncing…' : 'Sync now'}
          </button>
        </div>
      )}

      <ConnectAction
        platform={platform}
        meta={meta}
        connected={isConnected}
        onChange={onChange}
      />
    </div>
  )
}

function ConnectAction({
  platform,
  meta,
  connected,
  onChange,
}: {
  platform: AudiencePlatform
  meta: PlatformMeta
  connected: boolean
  onChange: () => void
}) {
  const c = meta.connect

  if (c.kind === 'auto') {
    return (
      <p className="text-xs italic text-zinc-500">
        {connected ? 'Auto-connected — no action required.' : 'Will auto-attach when prerequisites are met.'}
      </p>
    )
  }

  if (c.kind === 'disabled') {
    return (
      <button
        type="button"
        disabled
        title={c.reason}
        className="cursor-not-allowed rounded border border-zinc-200 bg-zinc-100 px-3 py-1.5 text-xs text-zinc-400"
      >
        Connect ({c.reason})
      </button>
    )
  }

  if (c.kind === 'oauth') {
    if (platform === 'ga4') {
      return (
        <div className="space-y-3">
          <OAuthConnectButton oauthStart={c.oauthStart} connected={connected} onChange={onChange} platform={platform} />
          {!connected && <Ga4ManualLink onChange={onChange} />}
        </div>
      )
    }
    return <OAuthConnectButton oauthStart={c.oauthStart} connected={connected} onChange={onChange} platform={platform} />
  }

  // verify-and-save dispatch
  if (platform === 'chartmetric') {
    return <ChartmetricConnectFlow verifyPath={c.verifyPath} connected={connected} onChange={onChange} />
  }
  return <SpotifyConnectFlow verifyPath={c.verifyPath} platform={platform} connected={connected} onChange={onChange} />
}

function describeGa4SkipReason(reason: string | null): string {
  switch (reason) {
    case 'no_properties':
      return 'this Google account has no GA4 properties (or no Viewer access on them).'
    case 'api_denied':
      return 'Google denied access to the Analytics Admin API — enable it in your Google Cloud project.'
    case 'unauthorized':
      return 'the Google token was not authorized for Analytics.'
    default:
      return reason
        ? `probe failed (${reason}).`
        : 'no GA4 property was auto-detected for this Google account.'
  }
}

function Ga4ManualLink({ onChange }: { onChange: () => void }) {
  const [propertyId, setPropertyId] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleLink() {
    const id = propertyId.trim()
    if (!/^\d+$/.test(id)) {
      setError('Enter the numeric Property ID from GA4 Admin → Property settings.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await artistFetch('/audience/google/link-ga4', {
        method: 'POST',
        body: { propertyId: id },
      })
      onChange()
      setPropertyId('')
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not link GA4 property')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-700">
      <p className="font-medium text-zinc-900">Link GA4 property manually</p>
      <p className="mt-1 text-zinc-600">
        If OAuth connected YouTube but skipped GA4, paste the Property ID here (uses the same Google login).
        Find it in GA4 → Admin → Property settings.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={propertyId}
          onChange={(e) => setPropertyId(e.target.value.replace(/\D/g, ''))}
          placeholder="e.g. 123456789"
          className="rounded border border-zinc-300 px-2 py-1.5 font-mono text-xs"
        />
        <button
          type="button"
          onClick={handleLink}
          disabled={saving || !propertyId.trim()}
          className="rounded bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
        >
          {saving ? 'Linking…' : 'Link property'}
        </button>
      </div>
      {error && <p className="mt-2 text-red-700">{error}</p>}
    </div>
  )
}

function OAuthConnectButton({
  oauthStart,
  connected,
  onChange,
  platform,
}: {
  oauthStart: string
  connected: boolean
  onChange: () => void
  platform: AudiencePlatform
}) {
  const [loading, setLoading] = useState(false)
  const handleConnect = async () => {
    setLoading(true)
    try {
      const result = await apiFetch<{ url: string }>(oauthStart)
      window.location.href = result.url
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(`Could not start OAuth: ${err instanceof Error ? err.message : 'unknown error'}`)
      setLoading(false)
    }
  }

  const handleDisconnect = async () => {
    if (!confirm(`Disconnect ${platform}?`)) return
    try {
      await artistFetch(`/audience/credentials/${platform}`, { method: 'DELETE' })
      onChange()
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(`Could not disconnect: ${err instanceof Error ? err.message : 'unknown error'}`)
    }
  }

  if (connected) {
    return (
      <button
        type="button"
        onClick={handleDisconnect}
        className="rounded border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
      >
        Disconnect
      </button>
    )
  }
  return (
    <button
      type="button"
      onClick={handleConnect}
      disabled={loading}
      className="rounded bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
    >
      {loading ? 'Opening consent…' : 'Connect'}
    </button>
  )
}

/**
 * Pull a Spotify artist ID out of a free-text input. Accepts:
 *   • bare 22-char base62 id
 *   • https://open.spotify.com/artist/<id>?si=...
 *   • https://artists.spotify.com/c/artist/<id>/home
 *   • spotify:artist:<id>
 * Returns null if no plausible ID found.
 */
function extractSpotifyArtistId(input: string): string | null {
  const trimmed = input.trim()
  if (!trimmed) return null
  // Spotify base62 IDs are 22 characters of A-Za-z0-9.
  const match = trimmed.match(/[A-Za-z0-9]{22}/)
  return match ? match[0] : null
}

function SpotifyConnectFlow({
  verifyPath,
  platform,
  connected,
  onChange,
}: {
  verifyPath: string
  platform: AudiencePlatform
  connected: boolean
  onChange: () => void
}) {
  const [input, setInput] = useState('')
  const [verified, setVerified] = useState<SpotifyVerifyResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const verify = async () => {
    const id = extractSpotifyArtistId(input)
    if (!id) {
      setError(
        'Could not find a Spotify artist ID in that. Paste the full URL from artists.spotify.com or open.spotify.com.',
      )
      return
    }
    setBusy(true)
    setError(null)
    setVerified(null)
    try {
      const r = await artistFetch<SpotifyVerifyResponse>(verifyPath, {
        method: 'POST',
        body: { artistId: id },
      })
      setVerified(r)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed')
    } finally {
      setBusy(false)
    }
  }

  const save = async () => {
    if (!verified) return
    setBusy(true)
    setError(null)
    try {
      await artistFetch('/audience/credentials', {
        method: 'POST',
        body: {
          platform: 'spotify',
          spotifyArtistId: verified.spotifyArtistId,
          displayLabel: verified.name,
        },
      })
      onChange()
      setInput('')
      setVerified(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setBusy(false)
    }
  }

  const disconnect = async () => {
    if (!confirm(`Disconnect ${platform}?`)) return
    try {
      await artistFetch(`/audience/credentials/${platform}`, { method: 'DELETE' })
      onChange()
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(`Could not disconnect: ${err instanceof Error ? err.message : 'unknown error'}`)
    }
  }

  if (connected) {
    return (
      <button
        type="button"
        onClick={disconnect}
        className="rounded border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
      >
        Disconnect
      </button>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Paste Spotify artist URL (or just the ID)"
          className="flex-1 rounded border border-zinc-300 px-2 py-1.5 text-xs focus:border-zinc-900 focus:outline-none"
        />
        <button
          type="button"
          onClick={verify}
          disabled={busy || input.trim().length === 0}
          className="rounded border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
        >
          {busy && !verified ? 'Verifying…' : 'Verify'}
        </button>
      </div>
      {verified && (
        <div className="flex items-center justify-between rounded border border-emerald-200 bg-emerald-50 px-3 py-2">
          <div className="flex items-center gap-2 text-xs">
            {verified.imageUrl && (
              <img src={verified.imageUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
            )}
            <div>
              <div className="font-medium text-zinc-900">{verified.name}</div>
              <div className="text-zinc-600">
                {verified.followers.toLocaleString()} followers · popularity {verified.popularity}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="rounded bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  )
}

// ── Chartmetric connect flow ─────────────────────────────────────────────────
//
// Chartmetric UI restricts artist discovery on lower subscription tiers, so
// rather than make the user hunt for an ID, we expose a typeahead that hits
// Chartmetric's /api/search endpoint directly. Backed by:
//   GET  /audience/search/chartmetric?q=…
//   POST /audience/verify/chartmetric  (final confirm before save)

interface ChartmetricSearchResult {
  cmArtistId: number
  name: string
  imageUrl: string | null
  cmArtistScore: number | null
  spotifyArtistId: string | null
}

interface ChartmetricVerifyResponse {
  chartmetricArtistId: number
  name: string
  spotifyMonthlyListeners: number | null
  cmArtistScore: number | null
  imageUrl?: string | null
}

function ChartmetricConnectFlow({
  verifyPath,
  connected,
  onChange,
}: {
  verifyPath: string
  connected: boolean
  onChange: () => void
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<ChartmetricSearchResult[]>([])
  const [verified, setVerified] = useState<ChartmetricVerifyResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [searching, setSearching] = useState(false)
  const [busy, setBusy] = useState(false)

  // Debounced search-as-you-type.
  useEffect(() => {
    if (verified) return
    const q = query.trim()
    if (q.length < 2) {
      setResults([])
      return
    }
    const handle = setTimeout(async () => {
      setSearching(true)
      try {
        const r = await artistFetch<ChartmetricSearchResult[]>(
          `/audience/search/chartmetric?q=${encodeURIComponent(q)}`,
        )
        setResults(r)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed')
      } finally {
        setSearching(false)
      }
    }, 300)
    return () => clearTimeout(handle)
  }, [query, verified])

  const verify = async (cmArtistId: number) => {
    setBusy(true)
    setError(null)
    setVerified(null)
    try {
      const r = await artistFetch<ChartmetricVerifyResponse>(verifyPath, {
        method: 'POST',
        body: { artistId: String(cmArtistId) },
      })
      setVerified(r)
      setResults([])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed')
    } finally {
      setBusy(false)
    }
  }

  const save = async () => {
    if (!verified) return
    setBusy(true)
    setError(null)
    try {
      await artistFetch('/audience/credentials', {
        method: 'POST',
        body: {
          platform: 'chartmetric',
          chartmetricArtistId: String(verified.chartmetricArtistId),
          displayLabel: verified.name,
        },
      })
      onChange()
      setQuery('')
      setResults([])
      setVerified(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setBusy(false)
    }
  }

  const disconnect = async () => {
    if (!confirm('Disconnect chartmetric?')) return
    try {
      await artistFetch('/audience/credentials/chartmetric', { method: 'DELETE' })
      onChange()
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(`Could not disconnect: ${err instanceof Error ? err.message : 'unknown error'}`)
    }
  }

  if (connected) {
    return (
      <button
        type="button"
        onClick={disconnect}
        className="rounded border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
      >
        Disconnect
      </button>
    )
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            if (verified) setVerified(null)
          }}
          placeholder="Search Chartmetric by artist name…"
          className="w-full rounded border border-zinc-300 px-2 py-1.5 text-xs focus:border-zinc-900 focus:outline-none"
        />
        {searching && (
          <span className="absolute right-2 top-1.5 text-xs italic text-zinc-400">searching…</span>
        )}
        {!verified && results.length > 0 && (
          <ul className="absolute z-10 mt-1 max-h-72 w-full overflow-y-auto rounded border border-zinc-200 bg-white shadow-lg">
            {results.map((r) => (
              <li key={r.cmArtistId}>
                <button
                  type="button"
                  onClick={() => verify(r.cmArtistId)}
                  disabled={busy}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-zinc-50 disabled:opacity-50"
                >
                  {r.imageUrl ? (
                    <img src={r.imageUrl} alt="" className="h-7 w-7 rounded-full object-cover" />
                  ) : (
                    <span className="inline-block h-7 w-7 rounded-full bg-zinc-200" />
                  )}
                  <span className="flex-1">
                    <span className="font-medium text-zinc-900">{r.name}</span>
                    {r.cmArtistScore != null && (
                      <span className="ml-2 text-zinc-500">CM score {r.cmArtistScore}</span>
                    )}
                  </span>
                  <span className="text-zinc-400">#{r.cmArtistId}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {verified && (
        <div className="flex items-center justify-between rounded border border-emerald-200 bg-emerald-50 px-3 py-2">
          <div className="flex items-center gap-2 text-xs">
            {verified.imageUrl && (
              <img src={verified.imageUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
            )}
            <div>
              <div className="font-medium text-zinc-900">{verified.name}</div>
              <div className="text-zinc-600">
                {verified.spotifyMonthlyListeners != null
                  ? `${verified.spotifyMonthlyListeners.toLocaleString()} monthly listeners`
                  : 'monthly listeners unavailable'}
                {verified.cmArtistScore != null && ` · CM score ${verified.cmArtistScore}`}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="rounded bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  )
}

// ── Surveys section ──────────────────────────────────────────────────────────

function SurveysSection() {
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [firingTemplateId, setFiringTemplateId] = useState<string | null>(null)

  const templatesQ = useQuery<SurveyTemplateRow[], ApiClientError>({
    queryKey: ['audience-survey-templates'],
    queryFn: () => artistFetch<SurveyTemplateRow[]>('/audience/surveys/templates'),
  })

  const campaignsQ = useQuery<SurveyCampaignRow[], ApiClientError>({
    queryKey: ['audience-survey-campaigns'],
    queryFn: () => artistFetch<SurveyCampaignRow[]>('/audience/surveys/campaigns'),
  })

  return (
    <section className="rounded-xl border border-zinc-200 bg-white">
      <header className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
        <div className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-zinc-500" />
          <h2 className="text-base font-semibold text-zinc-900">Fan surveys</h2>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-zinc-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-zinc-800"
        >
          <Plus className="h-4 w-4" />
          New template
        </button>
      </header>

      {/* Templates */}
      <div className="px-5 py-3">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Templates</h3>
        {templatesQ.data && templatesQ.data.length === 0 ? (
          <p className="rounded border border-dashed border-zinc-300 px-4 py-6 text-center text-sm text-zinc-500">
            No templates yet. Create one to start collecting first-party fan intelligence.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="py-2 font-medium">Name</th>
                <th className="py-2 font-medium">Cadence</th>
                <th className="py-2 text-center font-medium">Questions</th>
                <th className="py-2 text-center font-medium">Campaigns</th>
                <th className="py-2 text-right font-medium">Updated</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {(templatesQ.data ?? []).map((t) => (
                <tr key={t.id} className="text-zinc-700">
                  <td className="py-2.5">
                    <div className="font-medium text-zinc-900">{t.name}</div>
                    {t.description && <div className="text-xs text-zinc-500">{t.description}</div>}
                  </td>
                  <td className="py-2.5">{CADENCE_LABEL[t.cadence]}</td>
                  <td className="py-2.5 text-center">{t._count?.questions ?? 0}</td>
                  <td className="py-2.5 text-center">{t._count?.campaigns ?? 0}</td>
                  <td className="py-2.5 text-right text-xs text-zinc-500">{formatDate(t.updatedAt)}</td>
                  <td className="py-2.5 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setEditingTemplateId(t.id)}
                        className="rounded border border-zinc-200 px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setFiringTemplateId(t.id)}
                        className="inline-flex items-center gap-1 rounded bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700"
                      >
                        <Send className="h-3 w-3" />
                        Fire
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Campaigns */}
      <div className="border-t border-zinc-200 px-5 py-3">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Recent campaigns</h3>
        {campaignsQ.data && campaignsQ.data.length === 0 ? (
          <p className="rounded border border-dashed border-zinc-300 px-4 py-6 text-center text-sm text-zinc-500">
            No campaigns yet. Fire a template above to push it to HiveCells.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="py-2 font-medium">Template</th>
                <th className="py-2 font-medium">Status</th>
                <th className="py-2 font-medium">Window</th>
                <th className="py-2 text-right font-medium">Responses</th>
                <th className="py-2 text-right font-medium">Last aggregate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {(campaignsQ.data ?? []).slice(0, 20).map((c) => (
                <tr key={c.id} className="text-zinc-700">
                  <td className="py-2.5">
                    <div className="font-medium text-zinc-900">{c.template?.name ?? '—'}</div>
                    {c.lastErrorMessage && (
                      <div className="mt-0.5 text-xs text-red-600">{c.lastErrorMessage}</div>
                    )}
                  </td>
                  <td className="py-2.5">
                    <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ring-1 ${STATUS_TONE[c.status]}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="py-2.5 text-xs text-zinc-500">
                    {formatDate(c.windowStart)} → {formatDate(c.windowEnd)}
                  </td>
                  <td className="py-2.5 text-right tabular-nums">
                    {c.completedCount}
                    {c.targetSize !== null && <span className="text-zinc-400"> / {c.targetSize}</span>}
                  </td>
                  <td className="py-2.5 text-right text-xs text-zinc-500">
                    {c.lastAggregateAt ? formatDate(c.lastAggregateAt) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {creating && (
        <TemplateEditor
          mode="create"
          onClose={() => setCreating(false)}
        />
      )}
      {editingTemplateId && (
        <TemplateEditor
          mode="edit"
          templateId={editingTemplateId}
          onClose={() => setEditingTemplateId(null)}
        />
      )}
      {firingTemplateId && (
        <FireCampaignModal
          templateId={firingTemplateId}
          templates={templatesQ.data ?? []}
          onClose={() => setFiringTemplateId(null)}
        />
      )}
    </section>
  )
}

// ── Template editor (create + edit) ──────────────────────────────────────────

function emptyQuestion(position: number): SurveyQuestionInput {
  return {
    position,
    questionType: 'single_choice',
    prompt: '',
    isRequired: false,
    options: [
      { value: 'opt1', label: 'Option 1', bucket: 'opt1' },
      { value: 'opt2', label: 'Option 2', bucket: 'opt2' },
    ],
  }
}

function TemplateEditor({
  mode,
  templateId,
  onClose,
}: {
  mode: 'create' | 'edit'
  templateId?: string
  onClose: () => void
}) {
  const qc = useQueryClient()

  const existingQ = useQuery<SurveyTemplateDetail, ApiClientError>({
    queryKey: ['audience-survey-template', templateId],
    queryFn: () => artistFetch<SurveyTemplateDetail>(`/audience/surveys/templates/${templateId}`),
    enabled: mode === 'edit' && Boolean(templateId),
  })

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [cadence, setCadence] = useState<SurveyCadence>('ad_hoc')
  const [questions, setQuestions] = useState<SurveyQuestionInput[]>([emptyQuestion(0)])
  const [hydrated, setHydrated] = useState(mode === 'create')

  // Hydrate from existing template on first successful load.
  if (mode === 'edit' && existingQ.data && !hydrated) {
    setName(existingQ.data.name)
    setDescription(existingQ.data.description ?? '')
    setCadence(existingQ.data.cadence)
    setQuestions(
      existingQ.data.questions.map((q, i) => ({
        position: i,
        questionType: q.questionType,
        prompt: q.prompt,
        ...(q.subtext ? { subtext: q.subtext } : {}),
        ...(q.options ? { options: q.options } : {}),
        isRequired: q.isRequired,
        ...(q.affinityTag ? { affinityTag: q.affinityTag } : {}),
      })),
    )
    setHydrated(true)
  }

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        name,
        description: description || undefined,
        cadence,
        questions: questions.map((q, i) => ({ ...q, position: i })),
      }
      if (mode === 'create') {
        return artistFetch('/audience/surveys/templates', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      }
      return artistFetch(`/audience/surveys/templates/${templateId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['audience-survey-templates'] })
      if (templateId) qc.invalidateQueries({ queryKey: ['audience-survey-template', templateId] })
      onClose()
    },
  })

  function updateQuestion(idx: number, patch: Partial<SurveyQuestionInput>) {
    setQuestions((qs) => qs.map((q, i) => (i === idx ? { ...q, ...patch } : q)))
  }

  function updateOption(qIdx: number, oIdx: number, patch: Partial<QuestionOption>) {
    setQuestions((qs) =>
      qs.map((q, i) => {
        if (i !== qIdx) return q
        const opts = (q.options ?? []).map((o, j) => (j === oIdx ? { ...o, ...patch } : o))
        return { ...q, options: opts }
      }),
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="my-8 w-full max-w-3xl rounded-xl bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
          <h3 className="text-base font-semibold text-zinc-900">
            {mode === 'create' ? 'New survey template' : 'Edit survey template'}
          </h3>
          <button onClick={onClose} className="text-sm text-zinc-500 hover:text-zinc-900">
            Cancel
          </button>
        </header>
        <div className="space-y-5 px-5 py-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">
                Name
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded border border-zinc-300 px-2 py-1.5 text-sm"
                placeholder="Quarterly pulse Q2 2026"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">
                Cadence
              </label>
              <select
                value={cadence}
                onChange={(e) => setCadence(e.target.value as SurveyCadence)}
                className="w-full rounded border border-zinc-300 px-2 py-1.5 text-sm"
              >
                {Object.entries(CADENCE_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">
              Description
            </label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded border border-zinc-300 px-2 py-1.5 text-sm"
              placeholder="Optional context for the team"
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Questions ({questions.length})
              </h4>
              <button
                onClick={() => setQuestions((qs) => [...qs, emptyQuestion(qs.length)])}
                className="inline-flex items-center gap-1 rounded border border-zinc-200 px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
              >
                <Plus className="h-3 w-3" /> Add question
              </button>
            </div>
            <ul className="space-y-3">
              {questions.map((q, i) => (
                <li key={i} className="rounded border border-zinc-200 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium text-zinc-500">Q{i + 1}</span>
                    {questions.length > 1 && (
                      <button
                        onClick={() => setQuestions((qs) => qs.filter((_, j) => j !== i))}
                        className="text-zinc-400 hover:text-red-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-zinc-500">
                        Type
                      </label>
                      <select
                        value={q.questionType}
                        onChange={(e) => {
                          const nextType = e.target.value as SurveyQuestionType
                          const needsOptions = nextType === 'single_choice' || nextType === 'multi_choice'
                          setQuestions((qs) =>
                            qs.map((qq, idx) => {
                              if (idx !== i) return qq
                              const next: SurveyQuestionInput = {
                                position: qq.position,
                                questionType: nextType,
                                prompt: qq.prompt,
                                isRequired: qq.isRequired,
                              }
                              if (qq.subtext) next.subtext = qq.subtext
                              if (qq.affinityTag) next.affinityTag = qq.affinityTag
                              if (needsOptions) {
                                next.options = qq.options ?? [
                                  { value: 'opt1', label: 'Option 1', bucket: 'opt1' },
                                  { value: 'opt2', label: 'Option 2', bucket: 'opt2' },
                                ]
                              }
                              return next
                            }),
                          )
                        }}
                        className="w-full rounded border border-zinc-300 px-2 py-1 text-sm"
                      >
                        {Object.entries(QUESTION_TYPE_LABEL).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-zinc-500">
                        Affinity tag (optional)
                      </label>
                      <input
                        value={q.affinityTag ?? ''}
                        onChange={(e) => {
                          const v = e.target.value
                          setQuestions((qs) =>
                            qs.map((qq, idx) => {
                              if (idx !== i) return qq
                              const { affinityTag: _drop, ...rest } = qq
                              return v ? { ...rest, affinityTag: v } : rest
                            }),
                          )
                        }}
                        className="w-full rounded border border-zinc-300 px-2 py-1 text-sm"
                        placeholder="e.g. luxury_watch"
                      />
                    </div>
                  </div>
                  <div className="mt-2">
                    <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-zinc-500">
                      Prompt
                    </label>
                    <input
                      value={q.prompt}
                      onChange={(e) => updateQuestion(i, { prompt: e.target.value })}
                      className="w-full rounded border border-zinc-300 px-2 py-1 text-sm"
                      placeholder="Which of these watches would you wear?"
                    />
                  </div>
                  <div className="mt-2 flex items-center gap-3">
                    <label className="flex items-center gap-1.5 text-xs text-zinc-700">
                      <input
                        type="checkbox"
                        checked={q.isRequired}
                        onChange={(e) => updateQuestion(i, { isRequired: e.target.checked })}
                      />
                      Required
                    </label>
                  </div>
                  {(q.questionType === 'single_choice' || q.questionType === 'multi_choice') && (
                    <div className="mt-3">
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-[10px] font-medium uppercase tracking-wide text-zinc-500">
                          Options
                        </span>
                        <button
                          onClick={() =>
                            updateQuestion(i, {
                              options: [
                                ...(q.options ?? []),
                                { value: `opt${(q.options?.length ?? 0) + 1}`, label: '', bucket: `opt${(q.options?.length ?? 0) + 1}` },
                              ],
                            })
                          }
                          className="text-xs text-zinc-600 hover:text-zinc-900"
                        >
                          + Add option
                        </button>
                      </div>
                      <ul className="space-y-1">
                        {(q.options ?? []).map((o, j) => (
                          <li key={j} className="grid grid-cols-[1fr_1fr_auto] items-center gap-2">
                            <input
                              value={o.label}
                              onChange={(e) => updateOption(i, j, { label: e.target.value })}
                              className="rounded border border-zinc-300 px-2 py-1 text-sm"
                              placeholder="Label fans see"
                            />
                            <input
                              value={o.bucket}
                              onChange={(e) => updateOption(i, j, { bucket: e.target.value })}
                              className="rounded border border-zinc-300 px-2 py-1 text-sm font-mono"
                              placeholder="bucket-key"
                            />
                            {(q.options?.length ?? 0) > 2 && (
                              <button
                                onClick={() =>
                                  updateQuestion(i, {
                                    options: (q.options ?? []).filter((_, k) => k !== j),
                                  })
                                }
                                className="text-zinc-400 hover:text-red-600"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <footer className="flex items-center justify-between border-t border-zinc-200 px-5 py-3">
          {save.isError && (
            <span className="text-xs text-red-600">
              {(save.error as ApiClientError)?.message ?? 'Save failed'}
            </span>
          )}
          <div className="ml-auto flex gap-2">
            <button onClick={onClose} className="rounded border border-zinc-200 px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50">
              Cancel
            </button>
            <button
              onClick={() => save.mutate()}
              disabled={!name || save.isPending}
              className="rounded bg-zinc-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
            >
              {save.isPending ? 'Saving…' : mode === 'create' ? 'Create template' : 'Save changes'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}

// ── Fire campaign modal ──────────────────────────────────────────────────────

function FireCampaignModal({
  templateId,
  templates,
  onClose,
}: {
  templateId: string
  templates: SurveyTemplateRow[]
  onClose: () => void
}) {
  const qc = useQueryClient()
  const template = templates.find((t) => t.id === templateId)

  const today = new Date().toISOString().slice(0, 10)
  const inTwoWeeks = new Date(Date.now() + 14 * 24 * 3600 * 1000).toISOString().slice(0, 10)

  const [windowStart, setWindowStart] = useState(today)
  const [windowEnd, setWindowEnd] = useState(inTwoWeeks)
  const [cohortJson, setCohortJson] = useState('')

  const cohortValid = useMemo(() => {
    if (!cohortJson.trim()) return true
    try { JSON.parse(cohortJson); return true } catch { return false }
  }, [cohortJson])

  const fire = useMutation({
    mutationFn: async () => {
      const campaign = await artistFetch<{ id: string }>('/audience/surveys/campaigns', {
        method: 'POST',
        body: JSON.stringify({
          templateId,
          windowStart,
          windowEnd,
          cohortFilter: cohortJson.trim() ? JSON.parse(cohortJson) : undefined,
        }),
      })
      return artistFetch(`/audience/surveys/campaigns/${campaign.id}/fire`, { method: 'POST' })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['audience-survey-campaigns'] })
      qc.invalidateQueries({ queryKey: ['audience-survey-templates'] })
      onClose()
    },
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <header className="border-b border-zinc-200 px-5 py-3">
          <h3 className="text-base font-semibold text-zinc-900">Fire survey campaign</h3>
          <p className="text-xs text-zinc-500">{template?.name ?? '—'}</p>
        </header>
        <div className="space-y-3 px-5 py-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">
                Window start
              </label>
              <input
                type="date"
                value={windowStart}
                onChange={(e) => setWindowStart(e.target.value)}
                className="w-full rounded border border-zinc-300 px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">
                Window end
              </label>
              <input
                type="date"
                value={windowEnd}
                onChange={(e) => setWindowEnd(e.target.value)}
                className="w-full rounded border border-zinc-300 px-2 py-1.5 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">
              Cohort filter (JSON, optional)
            </label>
            <textarea
              value={cohortJson}
              onChange={(e) => setCohortJson(e.target.value)}
              rows={4}
              className={`w-full rounded border px-2 py-1.5 font-mono text-xs ${cohortValid ? 'border-zinc-300' : 'border-red-400'}`}
              placeholder={'{ "country": "GB", "tier": ["top", "second"] }'}
            />
            {!cohortValid && <p className="mt-1 text-xs text-red-600">Invalid JSON</p>}
          </div>
        </div>
        <footer className="flex items-center justify-between border-t border-zinc-200 px-5 py-3">
          {fire.isError && (
            <span className="text-xs text-red-600">
              {(fire.error as ApiClientError)?.message ?? 'Fire failed'}
            </span>
          )}
          <div className="ml-auto flex gap-2">
            <button onClick={onClose} className="rounded border border-zinc-200 px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50">
              Cancel
            </button>
            <button
              onClick={() => fire.mutate()}
              disabled={!cohortValid || fire.isPending}
              className="inline-flex items-center gap-1.5 rounded bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              {fire.isPending ? 'Firing…' : 'Fire campaign'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}

// ── Chartmetric insights (from latest weekly sync) ───────────────────────────

interface ChartmetricInsights {
  snapshotDate: string
  totalReach: number | null
  weeklyGrowth: number | null
  monthlyGrowth: number | null
  profile: Record<string, unknown> | null
  trend: Array<{ date: string; monthlyListeners: number | null }>
}

function ChartmetricInsightsSection() {
  const insightsQ = useQuery<ChartmetricInsights | null, ApiClientError>({
    queryKey: ['chartmetric-insights'],
    queryFn: async () => {
      const r = await artistFetch<ChartmetricInsights | null>('/audience/chartmetric/insights')
      return r
    },
  })

  const data = insightsQ.data
  if (!insightsQ.isLoading && !data) return null

  const p = (data?.profile ?? {}) as Record<string, unknown>
  const num = (k: string) => (typeof p[k] === 'number' ? (p[k] as number) : null)
  const trend = data?.trend ?? []
  const maxTrend = Math.max(1, ...trend.map((t) => t.monthlyListeners ?? 0))

  return (
    <section className="rounded-xl border border-zinc-200 bg-white">
      <header className="border-b border-zinc-200 px-5 py-3">
        <h2 className="text-base font-semibold text-zinc-900">Chartmetric snapshot</h2>
        <p className="text-xs text-zinc-500">
          Cross-platform stats from weekly sync — Apple Music, Deezer, career stage, listener trend.
        </p>
      </header>
      {insightsQ.isLoading && <p className="px-5 py-4 text-sm text-zinc-500">Loading…</p>}
      {data && (
        <div className="space-y-4 p-5">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatChip label="Monthly listeners" value={data.totalReach?.toLocaleString() ?? '—'} />
            <StatChip
              label="CM score"
              value={num('cmArtistScore') != null ? String(num('cmArtistScore')) : '—'}
            />
            <StatChip label="Career stage" value={String(p.careerStage ?? '—')} />
            <StatChip label="Momentum" value={String(p.careerMomentum ?? '—')} />
          </div>
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <StatChip label="Spotify followers" value={fmtNum(num('spotifyFollowers'))} />
            <StatChip label="Apple Music" value={fmtNum(num('appleMusicFollowers'))} />
            <StatChip label="Deezer fans" value={fmtNum(num('deezerFans'))} />
            <StatChip label="Instagram" value={fmtNum(num('instagramFollowers'))} />
            <StatChip label="TikTok" value={fmtNum(num('tiktokFollowers'))} />
            <StatChip label="YouTube" value={fmtNum(num('youtubeSubscribers'))} />
          </div>
          {(typeof p.appleMusicUrl === 'string' || typeof p.deezerUrl === 'string') && (
            <p className="text-xs text-zinc-600">
              {typeof p.appleMusicUrl === 'string' && (
                <a href={p.appleMusicUrl} className="mr-3 underline" target="_blank" rel="noreferrer">
                  Apple Music
                </a>
              )}
              {typeof p.deezerUrl === 'string' && (
                <a href={p.deezerUrl} className="underline" target="_blank" rel="noreferrer">
                  Deezer
                </a>
              )}
            </p>
          )}
          {trend.length > 1 && (
            <div>
              <p className="mb-2 text-xs font-medium text-zinc-700">Spotify monthly listeners (90d)</p>
              <div className="flex h-16 items-end gap-px">
                {trend.slice(-60).map((pt) => {
                  const v = pt.monthlyListeners ?? 0
                  const h = Math.max(2, Math.round((v / maxTrend) * 56))
                  return (
                    <div
                      key={pt.date}
                      className="flex-1 rounded-t bg-violet-500/80"
                      style={{ height: `${h}px` }}
                      title={`${pt.date}: ${v.toLocaleString()}`}
                    />
                  )
                })}
              </div>
              <p className="mt-1 text-[10px] text-zinc-500">
                {data.weeklyGrowth != null && (
                  <>Week: {data.weeklyGrowth >= 0 ? '+' : ''}{data.weeklyGrowth.toLocaleString()} · </>
                )}
                {data.monthlyGrowth != null && (
                  <>Month: {data.monthlyGrowth >= 0 ? '+' : ''}{data.monthlyGrowth.toLocaleString()}</>
                )}
                · Updated {new Date(data.snapshotDate).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>
      )}
    </section>
  )
}

function StatChip({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-2">
      <p className="text-[10px] font-medium uppercase tracking-wide text-zinc-500">{label}</p>
      <p className="mt-0.5 text-sm font-semibold tabular-nums text-zinc-900">{value}</p>
    </div>
  )
}

function fmtNum(n: number | null): string {
  return n != null ? n.toLocaleString() : '—'
}

// ── Similar artists (cached Chartmetric peers) ───────────────────────────────
//
// Manual-refresh card grid. Each refresh costs Chartmetric credits, so the
// button shows the £ estimate up-front and is admin/manager-only on the backend.

interface SimilarPeer {
  id: string
  cmArtistId: number
  name: string
  imageUrl: string | null
  spotifyArtistId: string | null
  cmArtistScore: number | null
  spotifyMonthlyListeners: number | null
  spotifyFollowers: number | null
  recentVenues?: Array<{
    venueName: string
    city: string | null
    countryCode: string | null
    eventName: string | null
    eventDate: string | null
  }> | null
  rank: number
  fetchedAt: string
}

interface SimilarArtistsPayload {
  peers: SimilarPeer[]
  fetchedAt: string | null
  nextRefreshEstimatePence: number
}

function SimilarArtistsSection() {
  const qc = useQueryClient()
  const [refreshing, setRefreshing] = useState(false)
  const [refreshError, setRefreshError] = useState<string | null>(null)

  const peersQ = useQuery<SimilarArtistsPayload, ApiClientError>({
    queryKey: ['audience-similar-artists'],
    queryFn: () => artistFetch<SimilarArtistsPayload>('/audience/similar-artists'),
  })

  const data = peersQ.data
  const peers = data?.peers ?? []
  const estimatePence = data?.nextRefreshEstimatePence ?? 12

  const refresh = async () => {
    const costLabel = `£${(estimatePence / 100).toFixed(2)}`
    if (!confirm(`Refreshing will fetch fresh data from Chartmetric and cost approximately ${costLabel}. Continue?`)) {
      return
    }
    setRefreshing(true)
    setRefreshError(null)
    try {
      await artistFetch('/audience/similar-artists/refresh', { method: 'POST' })
      await qc.invalidateQueries({ queryKey: ['audience-similar-artists'] })
    } catch (err) {
      setRefreshError(err instanceof Error ? err.message : 'Refresh failed')
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <section className="rounded-xl border border-zinc-200 bg-white">
      <header className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
        <div>
          <h2 className="text-base font-semibold text-zinc-900">Similar artists</h2>
          <p className="text-xs text-zinc-500">
            From Chartmetric — co-tour candidates, festival lineups, press targets. Refresh also pulls recent
            show cities for the top 3 peers (visible on the{' '}
            <a href="/audience/map" className="underline">fan map</a> overlay).
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          {data?.fetchedAt && (
            <span className="text-zinc-500">
              Updated {new Date(data.fetchedAt).toLocaleDateString()}
            </span>
          )}
          <button
            type="button"
            onClick={refresh}
            disabled={refreshing}
            className="rounded bg-zinc-900 px-3 py-1.5 font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
          >
            {refreshing ? 'Refreshing…' : `Refresh (~£${(estimatePence / 100).toFixed(2)})`}
          </button>
        </div>
      </header>

      {refreshError && (
        <p className="px-5 py-2 text-xs text-red-600">{refreshError}</p>
      )}

      <div className="p-5">
        {peers.length === 0 && !peersQ.isLoading && (
          <p className="text-sm italic text-zinc-500">
            No cached peers yet. Connect Chartmetric for this artist, then click
            Refresh to pull a similar-artists list.
          </p>
        )}
        {peers.length > 0 && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {peers.map((p) => (
              <div
                key={p.id}
                className="flex flex-col items-center gap-2 rounded-lg border border-zinc-200 p-3 text-center"
              >
                {p.imageUrl ? (
                  <img
                    src={p.imageUrl}
                    alt={p.name}
                    className="h-16 w-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-zinc-200 text-xl font-semibold text-zinc-500">
                    {p.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <div className="font-medium text-zinc-900">{p.name}</div>
                  {p.cmArtistScore != null && (
                    <div className="text-xs text-zinc-500">CM score {p.cmArtistScore}</div>
                  )}
                  {p.spotifyMonthlyListeners != null && (
                    <div className="text-xs text-zinc-500">
                      {p.spotifyMonthlyListeners.toLocaleString()} monthly
                    </div>
                  )}
                  {p.recentVenues && p.recentVenues.length > 0 && (
                    <ul className="mt-2 space-y-0.5 text-left text-[10px] text-zinc-500">
                      {p.recentVenues.slice(0, 3).map((v, idx) => (
                        <li key={idx}>
                          {v.venueName}
                          {v.city || v.countryCode
                            ? ` · ${[v.city, v.countryCode].filter(Boolean).join(', ')}`
                            : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

// ── Snapshots (placeholder until connectors land) ────────────────────────────

function SnapshotsSection() {
  const latestQ = useQuery<LatestSnapshot[], ApiClientError>({
    queryKey: ['audience-snapshots-latest'],
    queryFn: () => artistFetch<LatestSnapshot[]>('/audience/snapshots/latest'),
  })

  const rows = latestQ.data ?? []

  return (
    <section className="rounded-xl border border-zinc-200 bg-white">
      <header className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-zinc-500" />
          <h2 className="text-base font-semibold text-zinc-900">Latest snapshots</h2>
        </div>
        <span className="text-xs text-zinc-500">Per platform · most recent pull</span>
      </header>
      {rows.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-zinc-500">
          No snapshots yet. Connect a platform to start collecting audience signal.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="px-5 py-2 font-medium">Platform</th>
              <th className="px-5 py-2 text-right font-medium">Total reach</th>
              <th className="px-5 py-2 text-right font-medium">Engagement</th>
              <th className="px-5 py-2 text-right font-medium">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.map((r) => (
              <tr key={r.platform}>
                <td className="px-5 py-2.5 font-medium text-zinc-900">{PLATFORM_LABEL[r.platform]}</td>
                <td className="px-5 py-2.5 text-right tabular-nums">
                  {r.totalReach !== null ? r.totalReach.toLocaleString() : '—'}
                </td>
                <td className="px-5 py-2.5 text-right tabular-nums">
                  {r.engagementRate !== null ? `${(r.engagementRate * 100).toFixed(1)}%` : '—'}
                </td>
                <td className="px-5 py-2.5 text-right text-xs text-zinc-500">{formatDate(r.snapshotDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
